import {
  AnnouncementsFilter,
  CollectionAccessMode,
  CollectionType,
  DevicesInput,
  DevicesOrderBy,
  InputMaybe,
  Order,
  OrganizationsInput,
  UsersFilter,
} from '@/__generated__/graphql';
import { skipToken, useQuery, useSuspenseQuery } from '@apollo/client';
import STANDARD_AUTOCOMPLETE_DATA from '../queries/getStandardAutocompleteData';
import { DataAutocompleteType } from '../StandardAutocompleteIds';

/**
 * @deprecated use `useSuspenseAutocompleteOptions` instead
 */
export const useAutocompleteOptions = (
  dataType: DataAutocompleteType,
  queryOptions?: {
    includeGates?: boolean;
    withDgaDevices?: boolean;
    projectId?: string;
    organizationId?: number;
  },
) => {
  return useQuery(STANDARD_AUTOCOMPLETE_DATA, {
    variables: {
      includeOrganizations: dataType === 'organizations',
      includeDevices: dataType === 'devices',
      devicesInput: {
        orderBy: DevicesOrderBy.IdAsc,
        includeGates: queryOptions?.includeGates,
        project: queryOptions?.projectId,
      },
      organizationsInput: {
        withDgaDevices: queryOptions?.withDgaDevices,
      },
      projectsFilter: {
        organizationId: queryOptions?.organizationId,
      },
      includeIrrigations: dataType === 'irrigations',
      includeUsers: dataType === 'users',
      includeProjects: dataType === 'projectsPagination',
      includeCurrencies: dataType === 'currencies',
      includeServiceTypes: dataType === 'serviceTypes',
      includeAnnouncements: dataType === 'announcements',
      includeIntegrations: dataType === 'integrations',
      includeCollections: dataType === 'icnCollections',
      collectionsFilter: { types: [CollectionType.Icn], accessMode: CollectionAccessMode.DeviceDerived, sort: { field: 'name', order: Order.Asc } },
    },
  });
};

export const useSuspenseAutocompleteOptions = (
  type: DataAutocompleteType,
  queryOptions?: {
    includeGates?: boolean;
    withDgaDevices?: boolean;
    users?: InputMaybe<UsersFilter> | undefined;
    projectId?: string;
    organizationId?: number;
    devices?: InputMaybe<DevicesInput> | undefined;
    organizations?: InputMaybe<OrganizationsInput> | undefined;
    announcements?: InputMaybe<AnnouncementsFilter> | undefined;
  },
) => {
  return useSuspenseQuery(STANDARD_AUTOCOMPLETE_DATA, {
    variables: {
      includeOrganizations: type === 'organizations',
      includeDevices: type === 'devices',
      includeIrrigations: type === 'irrigations',
      includeUsers: type === 'users',
      includeProjects: type === 'projectsPagination',
      includeCurrencies: type === 'currencies',
      includeServiceTypes: type === 'serviceTypes',
      includeAnnouncements: type === 'announcements',
      includeIntegrations: type === 'integrations',
      includeCollections: type === 'icnCollections',
      devicesInput: {
        orderBy: DevicesOrderBy.IdAsc,
        includeGates: queryOptions?.includeGates,
        project: queryOptions?.projectId,
        ...queryOptions?.devices,
      },
      organizationsInput: {
        withDgaDevices: queryOptions?.withDgaDevices,
        ...queryOptions?.organizations,
      },
      usersInput: {
        ...queryOptions?.users,
      },
      projectsFilter: {
        organizationId: queryOptions?.organizationId,
      },
      announcementsInput: queryOptions?.announcements,
      collectionsFilter: { types: [CollectionType.Icn], accessMode: CollectionAccessMode.DeviceDerived, sort: { field: 'name', order: Order.Asc } },
    },
  });
};

export const useSuspenseAutocompleteSpecificValues = (
  type: DataAutocompleteType,
  value: string | number | string[] | number[] | (number | string)[] | null,
) => {
  const getIds = <T extends 'string' | 'number'>(entity: typeof type, target: T) => {
    type EntityId = T extends 'string' ? string : number;
    let entityIds: EntityId[] | null = null;
    if (type === entity) {
      if (typeof value === target) entityIds = [value as EntityId];
      if (Array.isArray(value))
        entityIds = value.filter((id) => typeof id === target) as EntityId[];
    }
    return entityIds?.length ? entityIds : null;
  };

  const deviceIds = getIds('devices', 'string');
  const userIds = getIds('users', 'number');
  const organizationIds = getIds('organizations', 'number');

  const skip = !(deviceIds?.length || userIds?.length || organizationIds?.length);

  return useSuspenseQuery(
    STANDARD_AUTOCOMPLETE_DATA,
    !skip
      ? {
        variables: {
          includeOrganizations: organizationIds ? type === 'organizations' : false,
          includeDevices: deviceIds ? type === 'devices' : false,
          includeUsers: userIds ? type === 'users' : false,
          includeIrrigations: false,
          includeProjects: false,
          includeCurrencies: false,
          includeServiceTypes: false,
          includeAnnouncements: false,
          includeIntegrations: false,
          includeCollections: false,
          devicesInput: { ids: deviceIds },
          organizationsInput: { ids: organizationIds },
          usersInput: { ids: userIds },
        },
      } : skipToken,
  );
};
